import React from 'react';
import { Button } from '@syneto/compass-react';
import { COMPONENT_CATEGORIES } from '../../../../constants/hardware-inventory';

/*
 * The four filters correspond to the four filterable table columns, so the bar can be read in the
 * same vocabulary as the rows. Manufacturer and model deliberately have no field of their own:
 * they are the API's parameters, not columns, and the component box resolves into them.
 *
 * `firmware` and `serialNumber` are exact matches server-side; the placeholders say so, because a
 * partial value silently returns nothing.
 */
const FleetInventoryFilters = (props) => {
	const { filters, smartQuery, smartSearch, hasActiveFilters, onSmartQueryChange, onFilterChange, onClearAll } = props;

	/*
	 * The box turns one phrase into the manufacturer/model pair the API can filter on, and there are
	 * no visible fields left to show what it picked. Saying it here keeps the box from being opaque -
	 * and in the ambiguous case, where the query cannot be expressed as a single AND-ed filter, it is
	 * the only thing distinguishing "too broad" from "the filter is broken".
	 */
	const renderSmartSearchHint = () => {
		switch (smartSearch?.status) {
			case 'type':
				return `Matching ${smartSearch.displayName}.`;
			case 'manufacturer':
				return smartSearch.matchCount
					? `Matching all ${smartSearch.matchCount} ${smartSearch.manufacturer} component types.`
					: `Matching manufacturers containing "${smartSearch.manufacturer}".`;
			case 'ambiguous':
				return `"${smartQuery.trim()}" matches ${smartSearch.matchCount} component types across `
					+ `${smartSearch.manufacturerCount} manufacturers, which cannot be filtered as one query. `
					+ 'Add more of the name to narrow it.';
			case 'model':
				return `Matching models containing "${smartSearch.value}".`;
			case 'unknown':
				return `No component type is named "${smartQuery.trim()}". Matching models containing `
					+ `"${smartSearch.value}" instead.`;
			default:
				return null;
		}
	};

	const hint = renderSmartSearchHint();

	return (
		<div className="mb-3">
			<div className="d-flex align-items-center gap-2">
				<input
					type="text"
					className="form-control"
					style={{ maxWidth: '390px' }}
					placeholder="Search a component, e.g. Syneto Hyperion or Intel Xeon"
					value={smartQuery}
					onChange={(event) => { return onSmartQueryChange(event.target.value); }}
				/>

				{hasActiveFilters && (
					<Button role="tertiary" onClick={onClearAll}>Clear all</Button>
				)}
			</div>

			{hint && (
				<p className={`mb-0 mt-1 ${smartSearch.status === 'ambiguous' ? 'text-warning' : 'text-muted'}`}>
					{hint}
				</p>
			)}

			<div className="d-flex align-items-center gap-2 flex-wrap mt-2">
				<select
					className="form-select"
					style={{ maxWidth: '170px' }}
					value={filters.category}
					onChange={(event) => { return onFilterChange('category', event.target.value); }}
				>
					<option value="">All categories</option>
					{COMPONENT_CATEGORIES.map((category) => {
						return <option key={category} value={category}>{category}</option>;
					})}
				</select>

				<input
					type="text"
					className="form-control"
					style={{ maxWidth: '220px' }}
					placeholder="Appliance serial (exact)"
					value={filters.serialNumber}
					onChange={(event) => { return onFilterChange('serialNumber', event.target.value); }}
				/>

				<input
					type="text"
					className="form-control"
					style={{ maxWidth: '200px' }}
					placeholder="Firmware version (exact)"
					value={filters.firmware}
					onChange={(event) => { return onFilterChange('firmware', event.target.value); }}
				/>
			</div>
		</div>
	);
};

export default FleetInventoryFilters;
